import { Link, useParams } from "react-router-dom";
import { usePublicSite } from "@/context/PublicSiteContext";
import { renderMarkdown } from "@/lib/markdown";
import Seo from "@/components/Seo";
import { metaDescription, siteOgImage, siteUrl, jsonLdForCmsPage } from "@/lib/siteMeta";
import ShareButton from "@/components/marketing/ShareButton";

/**
 * Renders a single CMS page by slug. Falls back to a small 404 when
 * the slug isn't in the published bundle.
 */
export default function SiteCmsPage() {
  const { orgSlug, pageSlug } = useParams();
  const { site } = usePublicSite();
  if (!site) return null;

  const page = (site.pages || []).find((p) => p.slug === pageSlug);
  const base = `/site/${orgSlug}`;
  const siteName = site.org.display_name || site.org.name;

  if (!page) {
    return (
      <>
        <Seo
          title={`Page not found · ${siteName}`}
          description="This page isn't published yet."
          canonical={siteUrl(orgSlug, `page/${pageSlug}`)}
          siteName={siteName}
        />
        <div data-testid="public-cms-page-missing" className="py-16 text-center">
          <h1 className="font-heading text-2xl font-semibold text-neutral-900">
            This page isn't published yet.
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            Try the <Link to={base} className="font-semibold underline">home page</Link>, or
            check back soon.
          </p>
        </div>
      </>
    );
  }

  const canonical = siteUrl(orgSlug, `page/${page.slug}`);

  return (
    <>
      <Seo
        title={`${page.title} · ${siteName}`}
        description={metaDescription(page.body_md, 160) || `Learn more about ${siteName}.`}
        canonical={canonical}
        ogImage={siteOgImage(orgSlug, "page", page.title)}
        ogType="article"
        themeColor={site.org.brand_primary_hex}
        siteName={siteName}
        jsonLd={jsonLdForCmsPage({ org: site.org, page, orgSlug })}
      />
      <article
        data-testid="public-cms-page"
        data-slug={page.slug}
        className="mx-auto max-w-3xl"
      >
      <nav
        aria-label="Breadcrumb"
        data-testid="public-cms-breadcrumb"
        className="mb-4 text-xs text-neutral-500"
      >
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link to={base} className="hover:text-neutral-800 hover:underline">
              {siteName}
            </Link>
          </li>
          <li aria-hidden="true" className="text-neutral-300">›</li>
          <li aria-current="page" className="font-semibold text-neutral-700">
            {page.title}
          </li>
        </ol>
      </nav>
      <header className="border-b border-neutral-200 pb-6">
        <p
          className="text-[10px] font-bold uppercase tracking-[0.18em]"
          style={{ color: "var(--color-primary)" }}
        >
          {site.org.display_name || site.org.name}
        </p>
        <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl">
          {page.title}
        </h1>
      </header>
      <div
        data-testid="public-cms-page-body"
        className="prose prose-neutral mt-8 max-w-none prose-headings:font-heading prose-h2:mt-8 prose-h2:text-2xl prose-p:leading-relaxed prose-a:text-[color:var(--color-primary)] prose-img:rounded-2xl"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(page.body_md) }}
      />
      <div className="mt-10 flex items-center justify-between border-t border-neutral-200 pt-6">
        <Link
          to={`/site/${orgSlug}`}
          className="text-sm font-semibold text-neutral-600 hover:text-neutral-900"
        >
          ← Back to home
        </Link>
        <ShareButton
          variant="ghost"
          url={canonical}
          title={`${page.title} · ${siteName}`}
          text={`Read this from ${siteName}`}
        />
      </div>
      </article>
    </>
  );
}
